// frontend/src/PriceEntry.tsx - Complete corrected file with debugging

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface PriceEntryProps {
  processId: number;
  processName: string;
  onComplete: () => void;
  onBack: () => void;
}

interface Supplier {
  id: number;
  name: string;
  abn_acn: string;
  total_offered_price: number | null;
  created_at: string;
}

interface SupplierPrice {
  supplier_id: number;
  price: string; // Using string for form input, will convert to number
}

const PriceEntry: React.FC<PriceEntryProps> = ({ processId, processName, onComplete, onBack }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierPrices, setSupplierPrices] = useState<SupplierPrice[]>([]);
  const [processStatus, setProcessStatus] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [screenSize, setScreenSize] = useState('desktop');

  // Responsive screen size detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('mobile');
      } else if (width < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load suppliers on component mount
  useEffect(() => {
    const loadSuppliers = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get('access_token') || 
                      Cookies.get('token') || 
                      Cookies.get('auth_token') || 
                      Cookies.get('jwt_token');
        
        // Also check localStorage as backup
        const localStorageToken = localStorage.getItem('access_token') || 
                                  localStorage.getItem('token') || 
                                  localStorage.getItem('auth_token');
        
        const finalToken = token || localStorageToken;

        if (!finalToken) {
          throw new Error('No access token found in cookies or localStorage');
        }

const response = await axios.get(`${API_URL}/api/processes/${processId}/suppliers`, {
  headers: { 
    'Authorization': `Bearer ${finalToken}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true
});
        const supplierData: Supplier[] = response.data;
        
        setSuppliers(supplierData);
        
        // Initialize price state with current prices
        const initialPrices: SupplierPrice[] = supplierData.map(supplier => ({
          supplier_id: supplier.id,
          price: supplier.total_offered_price?.toString() || ''
        }));
        setSupplierPrices(initialPrices);
        
      } catch (error) {
        console.error('Failed to load suppliers:', error);
        if (axios.isAxiosError(error)) {
          console.error('Axios error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
          });
        }
        showNotification('error', 'Failed to load suppliers. Check console for details.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSuppliers();
  }, [processId]);

  // Utility functions
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle price input changes
  const handlePriceChange = (supplierId: number, priceValue: string) => {
    setSupplierPrices(prev => 
      prev.map(sp => 
        sp.supplier_id === supplierId 
          ? { ...sp, price: priceValue }
          : sp
      )
    );
  };

  // Validate all prices
  const validatePrices = (): boolean => {
    for (const sp of supplierPrices) {
      const price = parseFloat(sp.price);
      if (isNaN(price) || price < 0) {
        showNotification('error', 'Please enter valid prices for all suppliers (must be 0 or greater)');
        return false;
      }
    }
    return true;
  };

  // Save all prices
  const savePrices = async (): Promise<boolean> => {
    if (!validatePrices()) {
      return false;
    }

    setIsSaving(true);
    try {
      // Save each supplier's price
      for (const sp of supplierPrices) {
        const price = parseFloat(sp.price);
        
        // Use same token discovery method as loadSuppliers
        const token = Cookies.get('access_token') || 
                      Cookies.get('token') || 
                      Cookies.get('auth_token') || 
                      Cookies.get('jwt_token') ||
                      localStorage.getItem('access_token') || 
                      localStorage.getItem('token') || 
                      localStorage.getItem('auth_token');


        if (!token) {
          throw new Error('No access token found for save operation');
        }

        await axios.put(
  `${API_URL}/api/processes/${processId}/suppliers/${sp.supplier_id}/price`,
  { total_offered_price: price },
  { 
    headers: { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    withCredentials: true
  }
);
      }
      
      showNotification('success', 'All supplier prices saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save prices:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
      }
      showNotification('error', 'Failed to save supplier prices. Check console for details.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save and return to dashboard
  const handleSavePrices = async () => {
    const success = await savePrices();
    if (success) {
      setTimeout(onBack, 1500); // Give time to see success message then return to dashboard
    }
  };

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  // Responsive breakpoints
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const isDesktop = screenSize === 'desktop';

  // Dynamic styles based on screen size
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      padding: isMobile ? '20px 16px' : isTablet ? '32px 24px' : '40px 32px',
      fontFamily: 'var(--font-family-primary)'
    },
    contentWrapper: {
      maxWidth: isMobile ? '100%' : isTablet ? '900px' : '1200px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: isMobile ? '16px' : isTablet ? '20px' : '24px',
      boxShadow: isMobile 
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid #f3f4f6',
      overflow: 'hidden'
    },
    header: {
      background: 'linear-gradient(45deg, #2563eb, #059669)',
      color: 'white',
      padding: isMobile ? '24px 20px' : isTablet ? '32px 32px' : '40px',
      textAlign: 'center' as const
    },
    headerTitle: {
      fontSize: isMobile ? '24px' : isTablet ? '32px' : '36px',
      fontWeight: 'bold',
      margin: '0 0 8px 0',
      lineHeight: '1.2'
    },
    headerSubtitle: {
      fontSize: isMobile ? '14px' : isTablet ? '16px' : '18px',
      opacity: 0.9,
      margin: '0',
      lineHeight: '1.4'
    },
    content: {
      padding: isMobile ? '24px 20px' : isTablet ? '32px 32px' : '40px'
    },
    sectionTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoCard: {
      backgroundColor: '#eff6ff',
      border: '2px solid #bfdbfe',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '16px' : isTablet ? '20px' : '24px',
      marginBottom: isMobile ? '24px' : '32px'
    },
    infoTitle: {
      fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
      fontWeight: '600',
      color: '#1e40af',
      margin: '0 0 8px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    infoText: {
      fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
      color: '#1e40af',
      margin: '0',
      lineHeight: '1.5'
    },
    supplierCard: {
      border: '2px solid #e5e7eb',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '16px' : isTablet ? '20px' : '24px',
      marginBottom: isMobile ? '16px' : '20px',
      backgroundColor: '#f9fafb',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    },
    supplierGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
      gap: isMobile ? '16px' : '24px',
      alignItems: 'center'
    },
    supplierInfo: {
      gridColumn: isMobile ? '1' : 'auto'
    },
    supplierName: {
      fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 4px 0'
    },
    supplierDetails: {
      fontSize: isMobile ? '13px' : isTablet ? '14px' : '15px',
      color: '#6b7280',
      margin: '0'
    },
    inputGroup: {
      gridColumn: isMobile ? '1' : 'auto'
    },
    label: {
      display: 'block',
      fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '12px 16px' : '14px 18px',
      border: '2px solid #d1d5db',
      borderRadius: isMobile ? '8px' : '10px',
      fontSize: isMobile ? '16px' : isTablet ? '17px' : '18px',
      transition: 'all 0.2s ease',
      outline: 'none',
      fontFamily: 'inherit'
    },
    priceDisplay: {
      gridColumn: isMobile ? '1' : 'auto',
      textAlign: isMobile ? 'left' as const : 'right' as const
    },
    priceAmount: {
      padding: isMobile ? '12px 16px' : '14px 18px',
      backgroundColor: '#f3f4f6',
      borderRadius: isMobile ? '8px' : '10px',
      fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
      fontWeight: '600',
      color: '#374151',
      border: '2px solid #e5e7eb'
    },
    summaryCard: {
      backgroundColor: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '20px' : isTablet ? '24px' : '28px',
      marginBottom: isMobile ? '24px' : '32px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: isMobile ? '12px' : '16px',
      marginBottom: '16px'
    },
    summaryItem: {
      padding: '12px 16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
    },
    summaryItemName: {
      fontSize: isMobile ? '14px' : '15px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '4px'
    },
    summaryItemPrice: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#059669'
    },
    summaryTotal: {
      padding: '12px 16px',
      backgroundColor: '#eff6ff',
      borderRadius: '8px',
      border: '2px solid #bfdbfe',
      textAlign: 'center' as const
    },
    warningCard: {
      backgroundColor: '#fffbeb',
      border: '2px solid #fde68a',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '16px' : isTablet ? '20px' : '24px',
      marginBottom: isMobile ? '24px' : '32px'
    },
    warningTitle: {
      fontSize: isMobile ? '16px' : isTablet ? '17px' : '18px',
      fontWeight: '600',
      color: '#92400e',
      margin: '0 0 8px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    warningList: {
      margin: '8px 0 0 20px',
      color: '#92400e',
      fontSize: isMobile ? '14px' : '15px',
      lineHeight: '1.5'
    },
    navigationContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' as const : 'row' as const,
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: isMobile ? '16px' : '0',
      paddingTop: '24px',
      borderTop: '2px solid #f3f4f6'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      flexDirection: isMobile ? 'column' as const : 'row' as const,
      width: isMobile ? '100%' : 'auto'
    },
    button: {
      padding: isMobile ? '14px 20px' : '12px 24px',
      borderRadius: isMobile ? '10px' : '8px',
      border: 'none',
      fontSize: isMobile ? '16px' : '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '120px',
      fontFamily: 'inherit'
    },
    backButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      order: isMobile ? 2 : 0
    },
    saveButton: {
      backgroundColor: '#0891b2',
      color: 'white'
    },
    primaryButton: {
      background: 'linear-gradient(45deg, #2563eb, #059669)',
      color: 'white'
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    notification: {
      padding: isMobile ? '14px 16px' : '16px 20px',
      marginBottom: '20px',
      borderRadius: isMobile ? '10px' : '12px',
      border: '2px solid',
      fontSize: isMobile ? '14px' : '15px',
      fontWeight: '500'
    },
    notificationSuccess: {
      backgroundColor: '#ecfdf5',
      borderColor: '#86efac',
      color: '#065f46'
    },
    notificationError: {
      backgroundColor: '#fef2f2',
      borderColor: '#fca5a5',
      color: '#991b1b'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid currentColor',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
      marginRight: '8px'
    },
    loadingContainer: {
      textAlign: 'center' as const,
      padding: isMobile ? '40px 20px' : '60px 40px',
      color: '#6b7280',
      fontSize: isMobile ? '16px' : '18px'
    }
  };

  const inputFocusStyle = {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
  };

  const buttonHoverStyle = {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          Loading supplier information...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          /* Remove number input spinner arrows */
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      
      <div style={styles.contentWrapper}>
        <div style={styles.content}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ margin: 0, fontSize: '2rem', color: '#1e293b' }}>Supplier Pricing</h1>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', color: '#6b7280' }}>{processName}</p>
          </div>
          {/* Notification */}
          {notification && (
            <div
              style={{
                ...styles.notification,
                ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
              }}
            >
              {notification.message}
            </div>
          )}

          {/* Supplier Price Entry */}
          <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
            <h2 style={styles.sectionTitle}>📋 Supplier Prices</h2>
            
            {suppliers.map((supplier, index) => {
              const supplierPrice = supplierPrices.find(sp => sp.supplier_id === supplier.id);
              const currentPrice = supplierPrice?.price || '';
              const numericPrice = parseFloat(currentPrice) || 0;
              
              return (
                <div key={supplier.id} style={styles.supplierCard}>
                  <div style={styles.supplierGrid}>
                    <div style={styles.supplierInfo}>
                      <h4 style={styles.supplierName}>{supplier.name}</h4>
                      <p style={styles.supplierDetails}>
                        ABN/ACN: {supplier.abn_acn}
                      </p>
                    </div>
                    
                    <div style={styles.inputGroup}>
                      <label htmlFor={`price-${supplier.id}`} style={styles.label}>
                        Total Offered Price (AUD) *
                      </label>
                      <input
                        id={`price-${supplier.id}`}
                        name={`price-${supplier.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={currentPrice}
                        onChange={(e) => handlePriceChange(supplier.id, e.target.value)}
                        placeholder="0.00"
                        style={styles.input}
                        required
                        onFocus={(e) => {
                          Object.assign(e.target.style, inputFocusStyle);
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    
                    <div style={styles.priceDisplay}>
                      {currentPrice && !isNaN(numericPrice) && (
                        <div style={styles.priceAmount}>
                          {formatCurrency(numericPrice)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div style={styles.summaryCard}>
            <h3 style={styles.sectionTitle}>📊 Price Summary</h3>
            <div style={styles.summaryGrid}>
              {suppliers.map(supplier => {
                const supplierPrice = supplierPrices.find(sp => sp.supplier_id === supplier.id);
                const currentPrice = parseFloat(supplierPrice?.price || '0') || 0;
                
                return (
                  <div key={supplier.id} style={styles.summaryItem}>
                    <div style={styles.summaryItemName}>{supplier.name}</div>
                    <div style={styles.summaryItemPrice}>
                      {currentPrice > 0 ? formatCurrency(currentPrice) : 'No price entered'}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div style={styles.summaryTotal}>
              <strong style={{ fontSize: isMobile ? '16px' : '18px' }}>
                Total Value: {formatCurrency(
                  supplierPrices.reduce((sum, sp) => sum + (parseFloat(sp.price) || 0), 0)
                )}
              </strong>
            </div>
          </div>

          {/* Important Notes */}
          <div style={styles.warningCard}>
            <h4 style={styles.warningTitle}>⚠️ Important Notes</h4>
            <ul style={styles.warningList}>
              <li>All prices should be in Australian Dollars (AUD)</li>
              <li>Enter the total price including all costs, fees, and taxes</li>
              <li>This pricing information will only be visible to Procurement Specialists</li>
              <li>Evaluators will not see any pricing information during their evaluation</li>
              <li>VFM calculations will be available after evaluations are complete</li>
            </ul>
          </div>

          {/* Navigation */}
          <div style={styles.navigationContainer}>
            <button
              type="button"
              onClick={onBack}
              style={{
                ...styles.button,
                ...styles.backButton
              }}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, buttonHoverStyle);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Back
            </button>
            
            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleSavePrices}
                disabled={isSaving}
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  ...(isSaving ? styles.buttonDisabled : {})
                }}
                onMouseEnter={(e) => {
                  if (!isSaving) {
                    Object.assign(e.currentTarget.style, buttonHoverStyle);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isSaving ? (
                  <>
                    <span style={styles.spinner}></span>
                    Saving...
                  </>
                ) : (
                  'Save Prices'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceEntry;